import { forwardRef, type FC, type PropsWithChildren } from "react";

import { IconClose } from "@/assets/icons/geist/IconClose";
import { Button, type ButtonProps } from "@/components/flexkit/Button";
import { Text } from "@/components/flexkit/typography/Text";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import type { PropsWithTestId } from "@/components/podkit/lib/testid";
import { type PropsWithTrackLocation } from "@/hooks/use-segment";

type NudgeProps = {
    open: boolean;
    toggleOpen: () => void;
    ariaLabelPrefix: string;
    showCloseButton?: boolean;
} & PropsWithTestId &
    PropsWithTrackLocation &
    PropsWithClassName &
    PropsWithChildren;

const NudgeRoot: FC<NudgeProps> = ({
    open,
    toggleOpen,
    ariaLabelPrefix,
    showCloseButton = true,
    className,
    children,
    "data-testid": testId,
    "data-track-location": trackLocation,
}) => {
    if (!open) {
        return null;
    }

    return (
        <div
            data-testid={testId}
            data-track-location={trackLocation}
            role="region"
            aria-live="polite"
            aria-label={ariaLabelPrefix}
            className={cn(
                "relative w-full max-w-[280px] flex-col rounded-2xl border-[0.5px] border-border-base/50 bg-surface-glass p-4 shadow-md backdrop-blur-modal",
                "animate-slideUpAndFadeIn",
                "hidden md:flex",
                className,
            )}
        >
            {showCloseButton && (
                <button
                    className="absolute right-4 top-4 z-10 inline-flex rounded-[4px] bg-transparent outline-none ring-0 hover:bg-surface-03 focus:ring-0 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-0 focus-visible:outline-content-primary active:outline-0 disabled:bg-transparent disabled:text-content-secondary"
                    onClick={toggleOpen}
                    aria-label={`${ariaLabelPrefix} - Dismiss`}
                    data-tracking-id={`dismiss-nudge`}
                >
                    <IconClose size="sm" />
                </button>
            )}
            {children}
        </div>
    );
};

const Content: FC<PropsWithChildren & PropsWithClassName> = ({ children, className }) => (
    <div className={cn("flex flex-col gap-3", className)}>{children}</div>
);

const Header: FC<PropsWithChildren & PropsWithClassName> = ({ children, className }) => (
    <div className={cn("flex flex-col gap-1 text-left", className)}>{children}</div>
);

const Title: FC<PropsWithChildren & PropsWithClassName> = ({ children, className }) => (
    <Text className={cn("text-base font-bold text-content-primary", className)}>{children}</Text>
);

const Description: FC<PropsWithChildren & PropsWithClassName> = ({ children, className }) => (
    <Text className={cn("text-base text-content-secondary", className)}>{children}</Text>
);

const Action = forwardRef<HTMLButtonElement, ButtonProps>(({ className, size = "md", ...props }, ref) => (
    <Button ref={ref} size={size} className={cn("grow", className)} {...props} />
));
Action.displayName = "Nudge.Action";

export const Nudge = Object.assign(NudgeRoot, {
    Content,
    Header,
    Title,
    Description,
    Action,
});
